import { AfterViewInit, ChangeDetectionStrategy, Component, EventEmitter, Input, OnDestroy, Output } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MessageService } from 'primeng/api';
import { Observable, map } from 'rxjs';
import { AuthService } from 'src/app/auth/auth.service';
import { CrimeGunService } from 'src/app/services/crime-gun.service';
import { AppConstants } from 'src/app/shared/app.constants';

@Component({
  selector: 'ness-crime-gun-triage',
  templateUrl: './crime-gun-triage.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CrimeGunTriageComponent implements AfterViewInit {
  @Output() onUpdateTriage = new EventEmitter<{ cgicHistory: ServerModels.CrimeGunTriageViewModel[], agencyHistory: ServerModels.CrimeGunTriageViewModel[] }>();
  @Input() canEditCGIC: boolean = false;
  @Input() canEditAgency: boolean = false;
  @Input() crimeGunId: string = null;
  @Input() defaultTriageValue: ServerModels.CrimeGunTriageViewModel = {
    CrimeGunTriageID: null,
    CrimeGunID: this.crimeGunId,
    TriageTierLevelID: null,
    Comment: null,
    AgencyID: null,
    CreatedOn: null
  };
  defaultTextAreaRows: number = AppConstants.defaultTextAreaRows;
  triageFormGroup: FormGroup = this.fb.group({
    CrimeGunTriageID: null,
    CrimeGunNumberID: [{ value: this.crimeGunId, disabled: true }, Validators.required],
    TriageTierLevelID: [null, Validators.required],
    Comment: ['', Validators.maxLength(1000)]
  });

  triageTierLevels$: Observable<ServerModels.ENESS_REF_TriageTierLevel[]> = this.crimeGunService.getTriageTierLevels().pipe(map((list: ServerModels.ENESS_REF_TriageTierLevel[]) => list.filter(x => x.TriageLevelName.toLowerCase() !== 'needs review')));

  get hasRole(): boolean {
    if (this.canEditCGIC) {
      return this.authService.hasUserTypeRole([{
        roles: ['NESS Administrator', 'Application Administrator', 'Power User', 'Editor'],
        userType: 'ATFFieldDivision'
      }]) || this.authService.hasUserTypeRole([{
        roles: ['NESS Administrator', 'Application Administrator', 'Power User', 'Editor'],
        userType: 'ATFHQ'
      }])
    } else if (this.canEditAgency) {
      return this.authService.hasUserTypeRole([{
        roles: ['Application Administrator', 'Editor'],
        userType: 'StateLocalLE'
      }]) && !this.authService.hasRole(['NESS Administrator']);
    } else {
      return false;
    }
  }

  constructor(private fb: FormBuilder,
    private messageService: MessageService,
    private crimeGunService: CrimeGunService,
    private authService: AuthService) { }

  ngAfterViewInit(): void {
    setTimeout(() => this._updateFormData(this.defaultTriageValue));
  }

  public canEditTriage(): boolean {
    return this.hasRole && (this.canEditCGIC || this.canEditAgency);
  }

  private _updateFormData(newFormData: ServerModels.CrimeGunTriageViewModel): void {
    if (!!newFormData && Object.keys(newFormData).length > 0) {
      this.triageFormGroup.reset({
        CrimeGunTriageID: null,
        CrimeGunNumberID: this.crimeGunId,
        TriageTierLevelID: null, // newFormData.TriageTierLevelID, D-17485 says should not default to current value 
        Comment: null
      });

      this.triageFormGroup.updateValueAndValidity();
    }
  }

  public onSubmitTriage(): void {
    if (this.triageFormGroup.valid) {
      let crimeGunTriage = <ServerModels.ENESS_CrimeGunTriage>this.triageFormGroup.getRawValue();
      crimeGunTriage.CrimeGunNumberID = this.crimeGunId;
      crimeGunTriage.AgencyID = this.canEditAgency ? this.authService.authOrgCode : null;

      this.crimeGunService.addCrimeGunTriage(crimeGunTriage).subscribe((resp: { cgicHistory: ServerModels.CrimeGunTriageViewModel[], agencyHistory: ServerModels.CrimeGunTriageViewModel[] }) => {
        this.onUpdateTriage.emit(resp);
        this.onResetTriage();
      },
        (err:any) => {
          this.messageService.add({ key: 'cgTriageMsg', severity: 'error', summary: '', detail: err.error });
        }
      );
    }
  }

  public onResetTriage(): void {
    this.triageFormGroup.reset({
      CrimeGunTriageID: null,
      CrimeGunNumberID: { value: this.crimeGunId, disabled: true },
      TriageTierLevelID: null,
      Comment: null
    });
  }

  public onTriageFormClick(clickEvent: Event) {
    clickEvent.stopPropagation();
  }
}
